import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded'
import LMTable from '../components/Tables/LMTable'
import {CellParams, LMColumn} from '../components/Tables/types'
import {useTranslation} from 'react-i18next'
import PageTitle from '../components/subcomponents/PageTitle'
import {TablePaper} from '../components/TablePaper'
import {compareCustomerNumbers, CUSTOMER_NUMBER_SORT_MODEL} from '../utils/customerNumberSort'
import {
  getCurrentBilling,
  updateLineItem,
  bulkUpdatePrices,
  approveBilling,
  generateBilling,
  cancelBilling,
  checkSnapshot,
  retryLineItemPush,
  retryAllFailedPushes,
  seedBilling,
  concludeBilling,
  getPastBillingRuns,
  getBillingRunById,
  PastBillingRun,
} from '../api/billing'
import {useUserRole} from '../utils/UserRoleContext'
import {ComputedBillingRun, ComputedBillingLineItem, isNonProductionKeycloakUrl} from 'sharedlibs'
import {shouldPollBillingRun, computePushProgress} from '../utils/pollingLogic'

const euro = (value: number) =>
  typeof value === 'number' && Number.isFinite(value) ? `${value.toFixed(2)} €` : '–'

// Ordering used when sorting the WeClapp status column: statuses that need
// attention (failed, skipped) rank first so they surface at the top of a run.
// Anything unknown sorts to the end.
const WECLAPP_STATUS_ORDER = ['failed', 'skipped', 'pending', 'pushing', 'success']
const weClappStatusRank = (status: string) => {
  const index = WECLAPP_STATUS_ORDER.indexOf(status)
  return index === -1 ? WECLAPP_STATUS_ORDER.length : index
}

export default function BillingPage() {
  const {t} = useTranslation()
  const {isSuperAdmin} = useUserRole()
  const keycloakConfig = sessionStorage.getItem('keycloakConfig')
  const isNonProduction = keycloakConfig
    ? isNonProductionKeycloakUrl(JSON.parse(keycloakConfig).url)
    : process.env.REACT_APP_ENV_STAGE === 'development'
  const showSeedButton = isNonProduction && isSuperAdmin
  const [billingRun, setBillingRun] = useState<ComputedBillingRun | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [seedDialogOpen, setSeedDialogOpen] = useState(false)

  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error'}>({
    open: false, message: '', severity: 'success'
  })
  const [approveDialogOpen, setApproveDialogOpen] = useState(false)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [concludeDialogOpen, setConcludeDialogOpen] = useState(false)
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false)
  const [pastRuns, setPastRuns] = useState<PastBillingRun[]>([])
  const [viewingPastRun, setViewingPastRun] = useState<ComputedBillingRun | null>(null)
  const [loadingPastRun, setLoadingPastRun] = useState(false)

  const now = new Date()
  const [generateMonth, setGenerateMonth] = useState(now.getMonth() + 1)
  const [generateYear, setGenerateYear] = useState(now.getFullYear())
  const [snapshotStatus, setSnapshotStatus] = useState<boolean | null>(null)
  const [rebuildFromMonthEnd, setRebuildFromMonthEnd] = useState(false)

  const [bulkSensorPrice, setBulkSensorPrice] = useState('')
  const [bulkImplantPrice, setBulkImplantPrice] = useState('')
  const [synchronizePrices, setSynchronizePrices] = useState(false)
  const [weClappStatusFilter, setWeClappStatusFilter] = useState<string>('all')

  const isPending = billingRun?.status === 'pending'

  // Push progress used to arrive as socket 'billing-push-progress' events;
  // with the run polled from Postgres it is derived from the row statuses of
  // the same snapshot the table renders. Gate logic lives in utils/pollingLogic.
  const pushProgress = computePushProgress(billingRun)

  const weightedSensorPrice = (() => {
    if (!billingRun) return 0
    const totalCorrected = billingRun.lineItems.reduce((s, li) => s + li.externalSensorsCorrected, 0)
    if (totalCorrected === 0) return 0
    return billingRun.lineItems.reduce((s, li) => s + li.externalSensorPrice * li.externalSensorsCorrected, 0) / totalCorrected
  })()

  const weightedImplantPrice = (() => {
    if (!billingRun) return 0
    const totalCorrected = billingRun.lineItems.reduce((s, li) => s + li.totalActiveImplantsCorrected, 0)
    if (totalCorrected === 0) return 0
    return billingRun.lineItems.reduce((s, li) => s + li.implantPrice * li.totalActiveImplantsCorrected, 0) / totalCorrected
  })()

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    // Load the current run and the past runs independently: a failure fetching
    // the (secondary) past runs must not blank out the current billing run.
    const [current, past] = await Promise.allSettled([getCurrentBilling(), getPastBillingRuns()])
    if (current.status === 'fulfilled') {
      setBillingRun(current.value)
    } else {
      setError(current.reason?.message ?? String(current.reason))
    }
    if (past.status === 'fulfilled') {
      setPastRuns(past.value)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    if (billingRun && billingRun.status !== 'pending') {
      setSynchronizePrices(billingRun.synchronizePricesToWeClapp)
    }
  }, [billingRun])

  // Silent refetch (no loading spinner) — used while following an in-flight
  // WeClapp push and after a synchronous single-row retry.
  const refreshCurrentRun = useCallback(async () => {
    try {
      setBillingRun(await getCurrentBilling())
    } catch (e) {
      console.error('Failed to refresh billing run:', e)
    }
  }, [])

  // Bulk retry is fire-and-forget server-side (the 202 returns before any
  // row has flipped to 'pushing'), so a single refetch can race the kickoff
  // and still see only 'failed' rows. This deadline keeps the poll alive
  // through that window until the rows start moving.
  const [forcePollUntil, setForcePollUntil] = useState(0)

  // While a WeClapp push is in flight (rows still pending/pushing on an
  // approved run) — or within the forced window after a bulk retry — poll
  // the run so row statuses move without a manual reload. The push status is
  // plain BillingLineItem state in Postgres, so this works from any backend
  // replica. billingRun is deliberately a dependency: every fetch re-arms
  // the interval, re-evaluating this gate so the poll stops on the cycle
  // after the last row settles (or the forced window lapses). Gate logic is
  // pure and unit-tested in utils/pollingLogic.
  useEffect(() => {
    if (!shouldPollBillingRun(billingRun, forcePollUntil, Date.now())) return
    const interval = setInterval(refreshCurrentRun, 3000)
    return () => clearInterval(interval)
  }, [billingRun, forcePollUntil, refreshCurrentRun])

  useEffect(() => {
    if (!generateDialogOpen) return
    setSnapshotStatus(null)
    setRebuildFromMonthEnd(false)
    checkSnapshot(generateMonth, generateYear)
      .then(setSnapshotStatus)
      .catch(() => setSnapshotStatus(null))
  }, [generateDialogOpen, generateMonth, generateYear])

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({open: true, message, severity})
  }

  const handleProcessRowUpdate = async (newRow: ComputedBillingLineItem, oldRow: ComputedBillingLineItem) => {
    const updates: Record<string, number> = {}
    if (newRow.externalSensorPrice !== oldRow.externalSensorPrice) updates.externalSensorPrice = newRow.externalSensorPrice
    if (newRow.implantPrice !== oldRow.implantPrice) updates.implantPrice = newRow.implantPrice
    if (newRow.extSensorsCorrection !== oldRow.extSensorsCorrection) updates.extSensorsCorrection = newRow.extSensorsCorrection
    if (newRow.implantCorrection !== oldRow.implantCorrection) updates.implantCorrection = newRow.implantCorrection
    if (newRow.extSensorsAmount !== oldRow.extSensorsAmount) updates.extSensorsAmount = newRow.extSensorsAmount
    if (newRow.implantsAmount !== oldRow.implantsAmount) updates.implantsAmount = newRow.implantsAmount

    if (Object.keys(updates).length === 0) return oldRow

    try {
      await updateLineItem(newRow.id, updates)
      await loadData()
      showSnackbar(t('activities.successfulSaving'), 'success')
    } catch (e: any) {
      showSnackbar(e.message || t('activities.failedSaving'), 'error')
      return oldRow
    }
    return newRow
  }

  const handleBulkSensorPrice = async () => {
    const price = parseFloat(bulkSensorPrice)
    if (isNaN(price) || !billingRun) return
    try {
      await bulkUpdatePrices(billingRun.id, {externalSensorPrice: price})
      await loadData()
      setBulkSensorPrice('')
      showSnackbar(t('activities.successfulSaving'), 'success')
    } catch (e: any) {
      showSnackbar(e.message || t('activities.failedSaving'), 'error')
    }
  }

  const handleBulkImplantPrice = async () => {
    const price = parseFloat(bulkImplantPrice)
    if (isNaN(price) || !billingRun) return
    try {
      await bulkUpdatePrices(billingRun.id, {implantPrice: price})
      await loadData()
      setBulkImplantPrice('')
      showSnackbar(t('activities.successfulSaving'), 'success')
    } catch (e: any) {
      showSnackbar(e.message || t('activities.failedSaving'), 'error')
    }
  }

  const handleApprove = async () => {
    if (!billingRun) return
    try {
      await approveBilling(billingRun.id, synchronizePrices)
      await loadData()
      setApproveDialogOpen(false)
      showSnackbar(t('pages.billingPage.approveSuccess'), 'success')
    } catch (e: any) {
      showSnackbar(e.message || t('activities.failedSaving'), 'error')
    }
  }

  const handleCancel = async () => {
    if (!billingRun) return
    try {
      await cancelBilling(billingRun.id)
      await loadData()
      setCancelDialogOpen(false)
      showSnackbar(t('pages.billingPage.cancelSuccess'), 'success')
    } catch (e: any) {
      showSnackbar(e.message || t('activities.failedSaving'), 'error')
    }
  }

  const handleRetryLineItemPush = useCallback(async (id: string) => {
    try {
      // The retry endpoint is synchronous — the outcome is final when it
      // returns, so one refetch shows it.
      await retryLineItemPush(id)
      await refreshCurrentRun()
    } catch (e: any) {
      showSnackbar(e.message || t('activities.failedSaving'), 'error')
    }
  }, [t, refreshCurrentRun])

  const handleRetryAllFailedPushes = async () => {
    if (!billingRun) return
    try {
      await retryAllFailedPushes(billingRun.id)
      // Force the poll on for a bounded window: the retried rows are still
      // 'failed' in our state (and possibly in the DB — the push is async),
      // so the pushInFlight gate alone would never start polling and the
      // grid would go stale until a manual reload.
      setForcePollUntil(Date.now() + 30_000)
      await refreshCurrentRun()
      showSnackbar(t('pages.billingPage.retryFailedStarted'), 'success')
    } catch (e: any) {
      showSnackbar(e.message || t('activities.failedSaving'), 'error')
    }
  }

  const handleConclude = async () => {
    if (!billingRun) return
    try {
      await concludeBilling(billingRun.id)
      await loadData()
      setConcludeDialogOpen(false)
      showSnackbar(t('pages.billingPage.concludeSuccess'), 'success')
    } catch (e: any) {
      showSnackbar(e.message || t('activities.failedSaving'), 'error')
    }
  }

  const handleOpenPastRun = async (id: string) => {
    setLoadingPastRun(true)
    try {
      const run = await getBillingRunById(id)
      setViewingPastRun(run)
    } catch (e: any) {
      showSnackbar(e.message || 'Failed to load billing run', 'error')
    }
    setLoadingPastRun(false)
  }

  const handleClosePastRun = () => {
    setViewingPastRun(null)
  }

  const hasFailedPushes = useMemo(() => {
    return billingRun?.lineItems.some(li => li.weClappPushStatus === 'failed') ?? false
  }, [billingRun])

  // DEV-ONLY: seed billing run with test data (remove when no longer needed)
  const handleSeed = async () => {
    try {
      await seedBilling()
      await loadData()
      setSeedDialogOpen(false)
      showSnackbar('Seed billing run created (May 2026)', 'success')
    } catch (e: any) {
      showSnackbar(e.message || 'Seed failed', 'error')
    }
  }

  const weClappStatusLabel = useCallback(
    (status: string) => t(`pages.billingPage.weClappStatus.${status}`, status),
    [t],
  )

  // Distinct WeClapp statuses present in the current run, ordered so problem
  // statuses come first — used to build the filter dropdown options.
  const availableWeClappStatuses = useMemo(() => {
    if (!billingRun) return []
    return Array.from(new Set(billingRun.lineItems.map(li => li.weClappPushStatus)))
      .sort((a, b) => weClappStatusRank(a) - weClappStatusRank(b))
  }, [billingRun])

  const displayedLineItems = useMemo(() => {
    if (!billingRun) return []
    if (weClappStatusFilter === 'all') return billingRun.lineItems
    return billingRun.lineItems.filter(li => li.weClappPushStatus === weClappStatusFilter)
  }, [billingRun, weClappStatusFilter])

  // Reset the filter when the selected status is no longer present (e.g. live
  // push updates resolve all failures), so the grid never shows an empty list
  // for a stale selection.
  useEffect(() => {
    if (weClappStatusFilter !== 'all' && !availableWeClappStatuses.includes(weClappStatusFilter)) {
      setWeClappStatusFilter('all')
    }
  }, [availableWeClappStatuses, weClappStatusFilter])

  const handleGenerate = async () => {
    try {
      await generateBilling(generateMonth, generateYear, rebuildFromMonthEnd ? 'monthEnd' : 'snapshot')
      await loadData()
      setGenerateDialogOpen(false)
      showSnackbar(t('pages.billingPage.generateSuccess'), 'success')
    } catch (e: any) {
      showSnackbar(e.message || t('activities.failedSaving'), 'error')
    }
  }

  const monthNames: Record<string, string[]> = {
    de: ['', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
    en: ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  }

  const getMonthName = (month: number) => {
    const lang = t('languages.de') === 'Deutsch' ? 'de' : 'en'
    return monthNames[lang]?.[month] ?? monthNames['de'][month]
  }

  function renderPastBillingRuns() {
    if (pastRuns.length === 0) return null
    return (
      <>
        <Divider sx={{mt: 4, mb: 2}}/>
        <Typography variant="h6" sx={{mb: 1}}>{t('pages.billingPage.pastRuns')}</Typography>
        {loadingPastRun && <Box sx={{display: "flex", justifyContent: "center", my: 2}}><CircularProgress size={24}/></Box>}
        {viewingPastRun && (
          <Box sx={{mb: 2}}>
            <Stack direction="row" spacing={2} sx={{alignItems: 'center', mb: 1}}>
              <Typography variant="h6">
                {getMonthName(viewingPastRun.month)} {viewingPastRun.year}
              </Typography>
              <Chip label={t('pages.billingPage.status.concluded')} color="default" size="small" variant="outlined"/>
              <Box sx={{flexGrow: 1}}/>
              <Button variant="outlined" size="small" onClick={handleClosePastRun}>
                {t('pages.billingPage.closePastRun')}
              </Button>
            </Stack>
            <TablePaper>
              <Box sx={{width: '100%'}}>
                <LMTable<ComputedBillingLineItem>
                  rows={viewingPastRun.lineItems}
                  columns={columns}
                  getRowId={(row) => row.id}
                  selectionMode="none"
                  options={{sortModel: CUSTOMER_NUMBER_SORT_MODEL}}
                  showFullContent
                />
              </Box>
            </TablePaper>
          </Box>
        )}
        {!viewingPastRun && (
          <TablePaper>
            <Box sx={{width: '100%'}}>
              <LMTable<PastBillingRun>
                rows={pastRuns}
                getRowId={(row) => row.id}
                columns={[
                  {
                    field: 'period', headerName: 'pages.billingPage.columns.period', width: 180,
                    valueGetter: (_value: unknown, row: PastBillingRun) => `${getMonthName(row.month)} ${row.year}`,
                  },
                  {
                    field: 'approvedAt', headerName: 'pages.billingPage.approvedAt', width: 160,
                    valueGetter: (_value: unknown, row: PastBillingRun) => row.approvedAt ? new Date(row.approvedAt).toLocaleDateString('de-DE') : '–',
                  },
                  {
                    field: 'approvedBy', headerName: 'pages.billingPage.approvedBy', width: 160,
                  },
                  {
                    field: 'concludedAt', headerName: 'pages.billingPage.concludedAt', width: 160,
                    valueGetter: (_value: unknown, row: PastBillingRun) => row.concludedAt ? new Date(row.concludedAt).toLocaleDateString('de-DE') : '–',
                  },
                ]}
                selectionMode="none"
                onRowClick={(row) => handleOpenPastRun(row.id)}
                hideFooter={pastRuns.length <= 10}
                pageSizeOptions={[10, 25]}
              />
            </Box>
          </TablePaper>
        )}
      </>
    )
  }

  const weClappStatusChipColor = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
    switch (status) {
      case 'success': return 'success'
      case 'failed': return 'error'
      case 'skipped': return 'info'
      case 'pending': return 'warning'
      case 'pushing': return 'info'
      default: return 'default'
    }
  }

  const columns: LMColumn<ComputedBillingLineItem>[] = [
    {
      field: 'weClappPushStatus',
      headerName: 'pages.billingPage.columns.weClappStatus',
      width: 140,
      sortComparator: (a: string, b: string) => weClappStatusRank(a) - weClappStatusRank(b),
      renderCell: (params: CellParams<ComputedBillingLineItem>) => {
        const row = params.row
        const status = row.weClappPushStatus
        const tooltip = [
          row.weClappPushError ? `Error: ${row.weClappPushError}` : null,
          row.weClappPushedAt ? `At: ${new Date(row.weClappPushedAt).toLocaleString('de-DE')}` : null,
          row.weClappInvoiceIds.length > 0 ? `Invoices: ${row.weClappInvoiceIds.join(', ')}` : null,
        ].filter(Boolean).join(' | ') || weClappStatusLabel(status)
        return (
          <Stack direction="row" spacing={0.5} sx={{alignItems: 'center'}}>
            <Tooltip title={tooltip}>
              <Chip label={weClappStatusLabel(status)} color={weClappStatusChipColor(status)} size="small" variant="outlined"/>
            </Tooltip>
            {status === 'failed' && (
              <Tooltip title={t('pages.billingPage.retryPush')}>
                <IconButton size="small" onClick={() => handleRetryLineItemPush(row.id)}>
                  <RefreshIcon fontSize="small"/>
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )
      },
    },
    {field: 'customerName', headerName: 'objects.customer.customerName', width: 180},
    {
      field: 'customerNumber', headerName: 'objects.customer.customerNumber', width: 120,
      sortComparator: compareCustomerNumbers,
    },
    {
      field: 'biotronikCustomerNumber', headerName: 'objects.customer.biotronikCustomerNumber', width: 140,
      sortComparator: compareCustomerNumbers,
    },
    {field: 'salesPerson', headerName: 'pages.billingPage.columns.salesPerson', width: 160},
    {
      field: 'extSensorsAmount', headerName: 'pages.billingPage.columns.extSensors', width: 110, type: 'number',
      editable: isPending,
    },
    {
      field: 'implantsAmount', headerName: 'pages.billingPage.columns.implants', width: 110, type: 'number',
      editable: isPending,
    },
    {field: 'implantsBiotronik', headerName: 'Biotronik', width: 90, type: 'number'},
    {field: 'implantsMedtronic', headerName: 'Medtronic', width: 90, type: 'number'},
    {field: 'implantsBoston', headerName: 'Boston', width: 80, type: 'number'},
    {field: 'implantsAbbott', headerName: 'Abbott', width: 80, type: 'number'},
    {field: 'implantsMicroPort', headerName: 'MicroPort', width: 90, type: 'number'},
    {
      field: 'extSensorsCorrection', headerName: 'pages.billingPage.columns.correctionSensors', width: 110, type: 'number',
      editable: isPending,
    },
    {
      field: 'implantCorrection', headerName: 'pages.billingPage.columns.correctionImplants', width: 110, type: 'number',
      editable: isPending,
    },
    {field: 'externalSensorsCorrected', headerName: 'pages.billingPage.columns.sensorsCorrected', width: 120, type: 'number'},
    {field: 'totalActiveImplantsCorrected', headerName: 'pages.billingPage.columns.implantsCorrected', width: 120, type: 'number'},
    {
      field: 'externalSensorPrice', headerName: 'pages.billingPage.columns.priceSensors', width: 120, type: 'number',
      editable: isPending,
      renderCell: (params: CellParams) => euro(params.value),
    },
    {
      field: 'implantPrice', headerName: 'pages.billingPage.columns.priceImplants', width: 120, type: 'number',
      editable: isPending,
      renderCell: (params: CellParams) => euro(params.value),
    },
    {
      field: 'totExtSensors', headerName: 'pages.billingPage.columns.totalExtSensors', width: 130, type: 'number',
      renderCell: (params: CellParams) => euro(params.value),
    },
    {
      field: 'totImplants', headerName: 'pages.billingPage.columns.totalImplants', width: 130, type: 'number',
      renderCell: (params: CellParams) => euro(params.value),
    },
    {
      field: 'getemedCostsPerPiece', headerName: 'pages.billingPage.columns.getemedCosts', width: 120, type: 'number',
      renderCell: (params: CellParams) => euro(params.value),
    },
    {
      field: 'extSensorsMarginSemdatex', headerName: 'SDX Marge (Sens.)', width: 130, type: 'number',
      renderCell: (params: CellParams) => euro(params.value),
    },
    {
      field: 'extSensorsTotMarginSemdatex', headerName: 'SDX Ges. (Sens.)', width: 130, type: 'number',
      renderCell: (params: CellParams) => euro(params.value),
    },
    {
      field: 'extSensorsMarginGetemed', headerName: 'GTM Marge (Sens.)', width: 130, type: 'number',
      renderCell: (params: CellParams) => euro(params.value),
    },
    {
      field: 'extSensorsMarginBio', headerName: 'BIO Marge (Sens.)', width: 130, type: 'number',
      renderCell: (params: CellParams) => euro(params.value),
    },
    {
      field: 'extSensorsMarginDC', headerName: 'DC Marge (Sens.)', width: 130, type: 'number',
      renderCell: (params: CellParams) => euro(params.value),
    },
    {
      field: 'getemedBillTotal', headerName: 'GTM Rechnung ges.', width: 140, type: 'number',
      renderCell: (params: CellParams) => euro(params.value),
    },
    {
      field: 'bioBillTotal', headerName: 'BIO Rechnung (Sens.)', width: 140, type: 'number',
      renderCell: (params: CellParams) => euro(params.value),
    },
    {
      field: 'dcBillTotal', headerName: 'DC Rechnung (Sens.)', width: 140, type: 'number',
      renderCell: (params: CellParams) => euro(params.value),
    },
    {
      field: 'semdatexImplantMarginEach', headerName: 'SDX Marge (Impl.)', width: 130, type: 'number',
      renderCell: (params: CellParams) => euro(params.value),
    },
    {
      field: 'semdatexImplantMarginTotal', headerName: 'SDX Ges. (Impl.)', width: 130, type: 'number',
      renderCell: (params: CellParams) => euro(params.value),
    },
    {
      field: 'bioImplantMarginEach', headerName: 'BIO Marge (Impl.)', width: 130, type: 'number',
      renderCell: (params: CellParams) => euro(params.value),
    },
    {
      field: 'dcImplantMarginEach', headerName: 'DC Marge (Impl.)', width: 130, type: 'number',
      renderCell: (params: CellParams) => euro(params.value),
    },
    {
      field: 'totBillBio', headerName: 'BIO Rechnung (Impl.)', width: 140, type: 'number',
      renderCell: (params: CellParams) => euro(params.value),
    },
    {
      field: 'totBillDC', headerName: 'DC Rechnung (Impl.)', width: 140, type: 'number',
      renderCell: (params: CellParams) => euro(params.value),
    },
  ]

  if (loading) {
    return (
      <div className="base-container">
        <PageTitle>{t('pages.billingPage.title')}</PageTitle>
        <Divider/>
        <Box sx={{display: "flex", justifyContent: "center", mt: 4}}><CircularProgress/></Box>
      </div>
    )
  }

  if (error) {
    return (
      <div className="base-container">
        <PageTitle>{t('pages.billingPage.title')}</PageTitle>
        <Divider/>
        <Alert severity="error" sx={{mt: 2}}>{error}</Alert>
      </div>
    )
  }

  if (!billingRun) {
    return (
      <div className="base-container">
        <PageTitle>{t('pages.billingPage.title')}</PageTitle>
        <Divider/>
        <Box sx={{mt: 4, textAlign: 'center'}}>
          <Typography variant="h6" color="text.secondary" sx={{mb: 2}}>
            {t('pages.billingPage.noInvoice')}
          </Typography>
          <Button variant="contained" color="primary" onClick={() => setGenerateDialogOpen(true)}>
            {t('pages.billingPage.generate')}
          </Button>
          {showSeedButton && (
            <Button variant="outlined" color="secondary" onClick={() => setSeedDialogOpen(true)} sx={{ml: 1}}>
              Seed Test Data (May 2026)
            </Button>
          )}
        </Box>
        {renderPastBillingRuns()}
        {renderGenerateDialog()}
        {renderSeedDialog()}
        {renderSnackbar()}
      </div>
    )
  }

  function renderGenerateDialog() {
    return (
      <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)}>
        <DialogTitle>{t('pages.billingPage.generate')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{mb: 2}}>{t('pages.billingPage.generateConfirm')}</DialogContentText>
          <Stack direction="row" spacing={2} sx={{mb: 1}}>
            <TextField
              select
              size="small"
              label={t('pages.billingPage.generateMonth')}
              value={generateMonth}
              onChange={e => setGenerateMonth(Number(e.target.value))}
              sx={{width: 150}}
            >
              {Array.from({length: 12}, (_, i) => (
                <MenuItem key={i + 1} value={i + 1}>{getMonthName(i + 1)}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label={t('pages.billingPage.generateYear')}
              type="number"
              value={generateYear}
              onChange={e => setGenerateYear(Number(e.target.value))}
              sx={{width: 100}}
            />
          </Stack>
          {snapshotStatus !== null && (
            <Chip
              label={snapshotStatus ? t('pages.billingPage.snapshotAvailable') : t('pages.billingPage.noSnapshot')}
              color={snapshotStatus ? 'success' : 'default'}
              size="small"
              variant="outlined"
            />
          )}
          {/* Only meaningful when a snapshot exists — without one, generation
              already reads the current configuration. */}
          {snapshotStatus === true && (
            <Box sx={{mt: 2}}>
              <FormControlLabel
                control={
                  <Switch
                    checked={rebuildFromMonthEnd}
                    onChange={e => setRebuildFromMonthEnd(e.target.checked)}
                  />
                }
                label={t('pages.billingPage.rebuildFromMonthEnd')}
              />
              <Typography variant="caption" color="text.secondary" sx={{display: "block"}}>
                {t('pages.billingPage.rebuildFromMonthEndHint')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialogOpen(false)}>{t('activities.cancel')}</Button>
          <Button onClick={handleGenerate} variant="contained" color="primary">{t('pages.billingPage.generate')}</Button>
        </DialogActions>
      </Dialog>
    )
  }

  // DEV-ONLY: seed confirmation dialog (remove when no longer needed)
  function renderSeedDialog() {
    return (
      <Dialog open={seedDialogOpen} onClose={() => setSeedDialogOpen(false)}>
        <DialogTitle>Seed Test Data</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will create a billing run for May 2026 with ~140 test customers and random data.
            An existing billing run for that month must be deleted first.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSeedDialogOpen(false)}>{t('activities.cancel')}</Button>
          <Button onClick={handleSeed} variant="contained" color="secondary">Seed</Button>
        </DialogActions>
      </Dialog>
    )
  }

  function renderSnackbar() {
    return (
      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar(s => ({...s, open: false}))}
        autoHideDuration={6000}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert onClose={() => setSnackbar(s => ({...s, open: false}))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    )
  }

  return (
    <div className="base-container">
      <PageTitle>{t('pages.billingPage.title')}</PageTitle>
      <Divider/>

      {/* Header with month/year and status */}
      <Stack direction="row" spacing={2} sx={{alignItems: 'center', mt: 2, mb: 2}}>
        <Typography variant="h5">
          {getMonthName(billingRun.month)} {billingRun.year}
        </Typography>
        <Chip
          label={isPending ? t('pages.billingPage.status.pending') : t('pages.billingPage.status.approved')}
          color={isPending ? 'warning' : 'success'}
          size="small"
          variant="outlined"
        />
        {billingRun.approvedAt && (
          <Typography variant="body2" color="text.secondary">
            {t('pages.billingPage.approvedAt')}: {new Date(billingRun.approvedAt).toLocaleDateString('de-DE')}
            {billingRun.approvedBy && ` (${billingRun.approvedBy})`}
          </Typography>
        )}
        <Box sx={{flexGrow: 1}}/>
        <Button variant="outlined" size="small" onClick={() => setGenerateDialogOpen(true)}>
          {t('pages.billingPage.generate')}
        </Button>
        {showSeedButton && (
          <Button variant="outlined" size="small" color="secondary" onClick={() => setSeedDialogOpen(true)}>
            Seed Test Data
          </Button>
        )}
      </Stack>

      {/* Sync prices toggle */}
      <Stack direction="row" sx={{alignItems: 'center', mb: 1}}>
        <Tooltip title={t('pages.billingPage.syncPricesTooltip')}>
          <FormControlLabel
            control={
              <Switch
                checked={synchronizePrices}
                onChange={e => setSynchronizePrices(e.target.checked)}
                disabled={!isPending}
              />
            }
            label={t('pages.billingPage.syncPrices')}
          />
        </Tooltip>
      </Stack>

      {/* Toolbar */}
      <Stack direction="row" spacing={1.5} useFlexGap sx={{alignItems: 'center', flexWrap: 'wrap', mb: 1}}>
        <Typography variant="body2" color="text.secondary">
          {t('pages.billingPage.weightedSensorPrice')}: <b>{euro(weightedSensorPrice)}</b>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('pages.billingPage.weightedImplantPrice')}: <b>{euro(weightedImplantPrice)}</b>
        </Typography>

        {availableWeClappStatuses.length > 1 && (
          <TextField
            select
            size="small"
            label={t('pages.billingPage.filterByStatus')}
            value={weClappStatusFilter}
            onChange={e => setWeClappStatusFilter(e.target.value)}
            sx={{width: 200}}
          >
            <MenuItem value="all">{t('pages.billingPage.allStatuses')}</MenuItem>
            {availableWeClappStatuses.map(status => (
              <MenuItem key={status} value={status}>{weClappStatusLabel(status)}</MenuItem>
            ))}
          </TextField>
        )}

        {!isPending && hasFailedPushes && (
          <Button variant="outlined" size="small" color="warning" onClick={handleRetryAllFailedPushes}>
            {t('pages.billingPage.retryAllFailed')}
          </Button>
        )}

        {isPending && (
          <>
            <Divider orientation="vertical" flexItem/>
            <TextField
              size="small"
              label={t('pages.billingPage.bulkSensorPrice')}
              type="number"
              value={bulkSensorPrice}
              onChange={e => setBulkSensorPrice(e.target.value)}
              sx={{width: 180}}
              slotProps={{input: {endAdornment: <Typography variant="body2">€</Typography>}}}
            />
            <Button variant="contained" size="small" onClick={handleBulkSensorPrice} disabled={!bulkSensorPrice}>
              {t('pages.billingPage.apply')}
            </Button>
            <TextField
              size="small"
              label={t('pages.billingPage.bulkImplantPrice')}
              type="number"
              value={bulkImplantPrice}
              onChange={e => setBulkImplantPrice(e.target.value)}
              sx={{width: 180}}
              slotProps={{input: {endAdornment: <Typography variant="body2">€</Typography>}}}
            />
            <Button variant="contained" size="small" onClick={handleBulkImplantPrice} disabled={!bulkImplantPrice}>
              {t('pages.billingPage.apply')}
            </Button>
            <Box sx={{flexGrow: 1}}/>
            <Button variant="outlined" color="error" size="small" onClick={() => setCancelDialogOpen(true)}>
              {t('pages.billingPage.cancel')}
            </Button>
            <Button variant="contained" color="primary" onClick={() => setApproveDialogOpen(true)}>
              {t('pages.billingPage.approve')}
            </Button>
          </>
        )}

        {!isPending && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArchiveRoundedIcon/>}
            onClick={() => setConcludeDialogOpen(true)}
          >
            {t('pages.billingPage.conclude')}
          </Button>
        )}
      </Stack>

      {/* WeClapp push progress */}
      {pushProgress && (
        <Box sx={{width: '100%', mb: 1}}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('pages.billingPage.pushProgress')} {pushProgress.completed} / {pushProgress.total}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={pushProgress.total ? (pushProgress.completed / pushProgress.total) * 100 : 0}
          />
        </Box>
      )}

      {/* Data Grid */}
      <TablePaper>
        <Box sx={{width: '100%'}}>
          <LMTable<ComputedBillingLineItem>
            rows={displayedLineItems}
            columns={columns}
            processRowUpdate={isPending ? handleProcessRowUpdate : undefined}
            getRowId={(row) => row.id}
            selectionMode="none"
            options={{sortModel: CUSTOMER_NUMBER_SORT_MODEL}}
            showFullContent
          />
        </Box>
      </TablePaper>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
        <DialogTitle>{t('pages.billingPage.approve')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('pages.billingPage.approveConfirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>{t('activities.cancel')}</Button>
          <Button onClick={handleApprove} variant="contained" color="primary">{t('pages.billingPage.approve')}</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>{t('pages.billingPage.cancel')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('pages.billingPage.cancelConfirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>{t('activities.cancel')}</Button>
          <Button onClick={handleCancel} variant="contained" color="error">{t('pages.billingPage.cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* Conclude Dialog */}
      <Dialog open={concludeDialogOpen} onClose={() => setConcludeDialogOpen(false)}>
        <DialogTitle>{t('pages.billingPage.conclude')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('pages.billingPage.concludeConfirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConcludeDialogOpen(false)}>{t('activities.cancel')}</Button>
          <Button onClick={handleConclude} variant="contained" color="primary">{t('pages.billingPage.conclude')}</Button>
        </DialogActions>
      </Dialog>

      {renderGenerateDialog()}
      {renderSeedDialog()}
      {renderSnackbar()}

      {renderPastBillingRuns()}
    </div>
  )
}
